/**
 * Helpers para evaluar estado de gastos fijos y compras MSI.
 */

const getToday = () => new Date();

/**
 * ¿El gasto fijo ya venció? Considera el mes/año que se está viendo, no solo
 * el día. Sin esto, mirar un mes futuro mostraba todos los fijos como vencidos
 * porque solo se comparaba `fechaLimite < hoy.getDate()`.
 *
 * @param viewedMonth (0-11) — opcional, default = mes actual.
 * @param viewedYear — opcional, default = año actual.
 */
export const isFijoVencido = (f, viewedMonth, viewedYear) => {
  if (f.pagado || !f.fechaLimite || !(f.monto > 0)) return false;
  const today = getToday();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  const vm = viewedMonth ?? currentMonth;
  const vy = viewedYear ?? currentYear;
  const viewedAbs = vy * 12 + vm;
  const currentAbs = currentYear * 12 + currentMonth;
  // Mes futuro: nada está vencido (los pagos aún no llegan)
  if (viewedAbs > currentAbs) return false;
  // Mes pasado: cualquier fijo no pagado ya venció
  if (viewedAbs < currentAbs) return true;
  // Mes actual: solo si la fecha límite ya pasó
  return Number(f.fechaLimite) < today.getDate();
};

/**
 * ¿El gasto fijo está por vencer (dentro de los próximos 3 días)?
 * Solo aplica al mes actual — un fijo del mes que viene no está "por vencer".
 */
export const isFijoPorVencer = (f, viewedMonth, viewedYear) => {
  if (f.pagado || !f.fechaLimite || !(f.monto > 0)) return false;
  const today = getToday();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  // Solo cuando estás viendo el mes actual
  if ((viewedMonth ?? currentMonth) !== currentMonth) return false;
  if ((viewedYear ?? currentYear) !== currentYear) return false;
  const dia = Number(f.fechaLimite);
  return dia >= today.getDate() && dia <= today.getDate() + 3;
};

/** Calcula las cuotas MSI activas para un mes+año dados (soporta cross-year) */
export const getMsiCuotasMes = (mes, msiCompras, anio) => {
  const currentYear = anio || new Date().getFullYear();
  // Convertir mes (0-11) + año a meses absolutos para comparar cross-year
  const targetAbsolute = currentYear * 12 + mes;
  return msiCompras
    .filter((c) => {
      const startYear = c.anioInicio || currentYear;
      const startAbsolute = startYear * 12 + c.mesInicio;
      return targetAbsolute >= startAbsolute && targetAbsolute < startAbsolute + c.meses;
    })
    .map((c) => {
      const startYear = c.anioInicio || currentYear;
      const startAbsolute = startYear * 12 + c.mesInicio;
      const cuotaN = targetAbsolute - startAbsolute + 1;
      return {
        ...c,
        cuotaN,
        cuota: Math.round((c.totalCompra / c.meses) * 100) / 100,
        pago: (c.pagos || {})[mes] || { pagado: false, montoPagado: null },
      };
    });
};
